import { atom, selector } from 'recoil'
import { Position } from '@turf/helpers'

import { keyFactory } from 'src/utils/recoil'
import { MapOverlay } from 'src/components/buildout-frame/types'
import { BBox2d, Day } from 'src/common-services'
import { Colors } from 'src/components/theme'
import {
  BuildoutTheme,
  BuildoutDemographicsConfig,
  BuildoutDriveTimeTypes,
} from 'src/common-services/types/partners-type'
import { ttaPosition } from 'src/atoms/buildout'
import { boundingBox as driveTimeBoundingBox } from 'src/atoms/drive-time'
import { boundingBox as radiusRingsBoundingBox } from 'src/atoms/radius-rings'

const key = keyFactory('buildout-frame')

export const token = atom<string>({
  key: key('token'),
  default: undefined,
})

export const loaded = atom<boolean>({
  key: key('loaded'),
  default: false,
})

export const center = atom<Position | undefined>({
  key: key('center'),
  default: undefined,
})

export const categories = atom<BuildoutDemographicsConfig['categories']>({
  key: key('categories'),
  default: {
    GeneralOverview: true,
    Overview: true,
    Households: true,
    HousingUnits: true,
    Gender: true,
    Ethnicity: true,
    Age: true,
    MaritalStatus: true,
    ValueOfOwnerOccupiedHousingUnits: true,
    TypesOfHousingUnits: true,
    HouseholdIncome: true,
    Education: true,
    LaborForce: true,
    TransportationToWork: true,
    TravelTimeToWork: true,
    PopulationByIndustry: true,
    PopulationByOccupation: true,
  },
})

export const initialDataConfig = atom<Pick<BuildoutDemographicsConfig, 'driveTime' | 'radiusRings'>>({
  key: key('initial-data-config'),
  default: {
    driveTime: {
      visible: true,
      day: Day.MONDAY,
      type: BuildoutDriveTimeTypes.ARRIVAL,
      durations: [5, 10, 15],
    },
    radiusRings: {
      visible: true,
      radii: [1, 3, 5],
    },
  },
})

export const dataConfigOverride = atom<Pick<BuildoutDemographicsConfig, 'driveTime' | 'radiusRings'>>({
  key: key('data-config-override'),
  default: undefined,
})

export const dataConfig = selector({
  key: key('data-config'),
  get: ({ get }) => {
    const initialConfig = get(initialDataConfig)
    const configOverride = get(dataConfigOverride)
    return configOverride || initialConfig
  },
})

export const theme = atom<BuildoutTheme>({
  key: key('theme'),
  default: {
    primaryColor: Colors.blueDark,
    // secondaryColor: Colors.bgMedium,
  },
})

export const mapOverlay = atom<MapOverlay>({
  key: key('map-overlay'),
  default: MapOverlay.RadiusRings,
})

export const hideMarker = atom<boolean>({
  key: key('hide-marker'),
  default: false,
})

export const mapBBox = selector<BBox2d | undefined>({
  key: key('map-bbox'),
  get: ({ get }) => {
    const overlay = get(mapOverlay)
    if (overlay === MapOverlay.DriveTime) {
      return get(driveTimeBoundingBox)
    }
    if (overlay === MapOverlay.RadiusRings) {
      const position = get(ttaPosition)
      return get(radiusRingsBoundingBox(position))
    }
    return
  },
})
